import { AGENT_WRITE_SUPERSEDED_REASON } from '@atbash-labs/proxy-client'

import { agentWritesApi } from '../api/agents/agent-writes'
import {
  removeInFlightAgentActionsForPlan,
  type InFlightAgentActionProposal
} from '../slices/agent-orchestrator-slice'
import type { AppDispatch, RootState } from '../store'
import { getAgentActionLifecycle, resumeRowLineFor } from './agent-actions/lifecycle-registry'
import { runOntologyAgentOrchestrator } from './run-agent-orchestrator'

interface SettledRow {
  toolUseId: string
  proposal: InFlightAgentActionProposal
}

/**
 * Build the natural-language follow-up instruction the orchestrator
 * receives after every row of a plan settles. The agent already has the
 * original user prompt + its own "I've proposed N changes" summary in
 * `priorTurns`, so the resume turn just needs to relay the per-row
 * decisions back to the model. The per-row text is produced by each
 * proposal's registered capability (`resumeRowLineFor`) so this stays
 * agnostic to the action kind.
 */
export function buildResumeInstruction(planId: string, rows: SettledRow[]): string {
  const ordered = [...rows].sort((a, b) => {
    const ai = a.proposal.batchIndex ?? 0
    const bi = b.proposal.batchIndex ?? 0
    return ai - bi
  })

  const lines: string[] = []
  lines.push(`[user decisions on proposal ${planId}]`)
  ordered.forEach((row, idx) => {
    lines.push(resumeRowLineFor(row.proposal, idx + 1))
  })
  lines.push('')
  lines.push(
    'Please confirm what was applied, acknowledge any rejections, and let me know if you want to revise the rejected items.'
  )
  return lines.join('\n')
}

function rowsForPlan(state: RootState, planId: string): SettledRow[] {
  const rows: SettledRow[] = []
  // With multi-tab (AXI-3530) cypher-write rows live per-tab. A plan's
  // rows are always created from one conversation's stream, so they'll
  // be on a single tab — but searching across tabs keeps the function
  // simple and resilient to weird edge cases (e.g. a plan that survives
  // a tab close + reopen via hydrate).
  for (const tab of Object.values(state.agentOrchestrator.tabs)) {
    for (const [toolUseId, proposal] of Object.entries(tab.inFlightAgentActions)) {
      if (proposal.planId === planId) rows.push({ toolUseId, proposal })
    }
  }
  return rows
}

function allTerminal(rows: SettledRow[]): boolean {
  return rows.length > 0 && rows.every((r) => r.proposal.terminalState !== undefined)
}

const inFlightResumeClaims = new Set<string>()

/**
 * `true` while the conversation's tab has an actively-streaming run —
 * i.e. a live SSE stream that hasn't finished, failed, or been stopped.
 * `failTabRun` keeps `tab.run` around for its banner, so a bare
 * null-check would read that terminal state as "streaming".
 */
function isConversationStreaming(state: RootState, conversationId: string): boolean {
  const run = state.agentOrchestrator.tabs[conversationId]?.run
  return run !== null && run !== undefined && run.streamError === null
}

/**
 * FALLBACK rail for delivering decisions to the agent. On the primary
 * (blocking) path the propose tool stalls in-turn, wins the settlement
 * claim server-side, and relays the outcomes in the same stream — this
 * thunk's claim then loses (or never fires, because the settled
 * `tool_call_complete` retired the cards first). It delivers the
 * decisions only when in-turn delivery couldn't happen: the run was
 * stopped or timed out mid-wait, the server restarted, or the decisions
 * arrived after the stream ended.
 *
 * Check whether every row in `planId` has reached a terminal state. If
 * so, claim the right to fire the synthetic follow-up agent turn from
 * the proxy (idempotent via the `resume_dispatched_at` column) and only
 * fire on the unique winner. The cards for the plan are removed from
 * `inFlightCypherWrites` once the resume has been dispatched.
 *
 * Deferred while the conversation's run is still streaming: a live
 * stream means a blocked executor may own the settlement, and firing
 * the resume turn then would open a second concurrent orchestrator run
 * on the same tab — the first stream's completion clears the shared
 * live row and the half-done resume run surfaces through the
 * post-stream refetch as a contentless past turn. The post-stream
 * sequence in `runOntologyAgentOrchestrator` re-dispatches deferred
 * plans via {@link fireSettledAgentActionPlans} once the stream
 * settles.
 */
export function maybeFireAgentActionPlanResume(planId: string, conversationId: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    if (!planId) return
    if (inFlightResumeClaims.has(planId)) return
    if (isConversationStreaming(getState(), conversationId)) return

    const rows = rowsForPlan(getState(), planId)
    if (!allTerminal(rows)) return

    inFlightResumeClaims.add(planId)
    try {
      const claim = await dispatch(
        agentWritesApi.endpoints.claimCypherWriteResumeDispatch.initiate({
          planId,
          conversationId
        })
      ).unwrap()
      if (!claim.claimed) {
        // Another tab / refresh already fired the resume turn. Drop
        // the cards locally so this tab matches the durable state.
        dispatch(removeInFlightAgentActionsForPlan({ planId }))
        return
      }

      // Re-read after the (async) claim — the in-memory rows may have
      // gained late metadata between the terminal-check above and now.
      const finalRows = rowsForPlan(getState(), planId)
      const instruction = buildResumeInstruction(planId, finalRows)

      // Drop the cards before kicking off the new orchestrator turn so
      // the sidebar visibly transitions back to "thinking" without the
      // settled cards lingering.
      dispatch(removeInFlightAgentActionsForPlan({ planId }))

      await dispatch(runOntologyAgentOrchestrator({ instruction, conversationId }))
    } catch (err) {
      console.warn('[agent-orchestrator] cypher-write resume claim/dispatch failed', err)
    } finally {
      inFlightResumeClaims.delete(planId)
    }
  }
}

/**
 * Re-dispatch the resume check for every plan with rows on the given
 * conversation's tab. Called from the orchestrator thunk once a stream
 * settles (completed or failed) to pick up plans whose last row was
 * settled mid-stream — `maybeFireAgentActionPlanResume` defers while
 * the stream is live. On the blocking happy path this no-ops without
 * touching the claim endpoint: the propose tool's settled
 * `tool_call_complete` retired the plan's cards before the stream
 * ended, so no rows remain. Each dispatch self-guards
 * (all-rows-terminal check + server-side claim), so plans with
 * still-actionable rows and already-claimed plans are no-ops.
 */
export function fireSettledAgentActionPlans(conversationId: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const tab = getState().agentOrchestrator.tabs[conversationId]
    if (tab === undefined) return
    const planIds = new Set<string>()
    for (const proposal of Object.values(tab.inFlightAgentActions)) {
      if (proposal.planId) planIds.add(proposal.planId)
    }
    for (const planId of planIds) {
      await dispatch(maybeFireAgentActionPlanResume(planId, conversationId))
    }
  }
}

/**
 * Reject every still-actionable in-flight cypher-write proposal on the
 * given conversation's tab server-side. Used by the chat controller
 * whenever the user sends a message, so that proposals the user neither
 * approved nor rejected get marked `rejected` durably (instead of
 * staying `awaiting_user` and re-appearing via the post-turn
 * `hydratePendingCypherWrites` call). A message sent during a live turn
 * is queued behind that turn, and a propose tool blocked on an
 * undecided card would otherwise hold the queue indefinitely.
 *
 * Multi-tab (AXI-3530): only touches the named conversation's tab.
 * Other tabs' open proposals are untouched — the user may still be
 * deciding on those independently.
 *
 * Each reject is fired in parallel; individual failures are swallowed
 * via `Promise.allSettled` so a flaky network on one row doesn't block
 * the new turn. Surviving `awaiting_user` rows just come back through
 * hydrate and the user can act on them again — same recoverable state
 * as a fresh page load.
 *
 * Returns the number of rows that were submitted to the reject API
 * (regardless of success), so callers can no-op cheaply when nothing
 * was abandoned.
 */
export function rejectAbandonedInFlightAgentActions(conversationId: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<number> => {
    const tab = getState().agentOrchestrator.tabs[conversationId]
    if (tab === undefined) return 0
    const targets: Array<{ toolUseId: string; kind: string }> = []
    for (const [toolUseId, proposal] of Object.entries(tab.inFlightAgentActions)) {
      if (proposal.terminalState === undefined) targets.push({ toolUseId, kind: proposal.kind })
    }
    if (targets.length === 0) return 0
    await Promise.allSettled(
      targets.map(({ toolUseId, kind }) => {
        const capability = getAgentActionLifecycle(kind)
        if (!capability) return Promise.resolve()
        return dispatch(capability.reject(toolUseId, AGENT_WRITE_SUPERSEDED_REASON))
      })
    )
    return targets.length
  }
}

/**
 * Offline rail: on conversation load, ask the proxy for plans that
 * settled without their outcome ever reaching an agent (browser closed
 * mid-decision, blocked run stopped or orphaned) and claim+dispatch
 * each. Idempotent: the proxy's `resume_dispatched_at` guard ensures
 * only one tab wins per plan.
 *
 * Callers must not race a live run's blocked executor — only dispatch
 * this when no orchestrator loop is live for the conversation: on load
 * when no run is persisted `running`, or after the `/resume` re-attach
 * 404s (run persisted `running` but no live loop exists server-side).
 */
export function dispatchUnresumedAgentActionPlans(conversationId: string) {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (!conversationId) return
    let plans
    try {
      plans = await dispatch(
        agentWritesApi.endpoints.listSettledUnresumedCypherPlans.initiate(
          { conversationId },
          { forceRefetch: true }
        )
      ).unwrap()
    } catch (err) {
      console.warn('[agent-orchestrator] settled-unresumed cypher-plan list failed', err)
      return
    }
    if (!plans || plans.length === 0) return

    for (const plan of plans) {
      if (inFlightResumeClaims.has(plan.planId)) continue
      inFlightResumeClaims.add(plan.planId)
      try {
        const claim = await dispatch(
          agentWritesApi.endpoints.claimCypherWriteResumeDispatch.initiate({
            planId: plan.planId,
            conversationId
          })
        ).unwrap()
        if (!claim.claimed) continue

        // Hydrate-on-load already populated the `inFlightCypherWrites`
        // dict from `/agent-writes/pending` for awaiting_user rows; for
        // settled-unresumed plans we don't have per-row terminal data
        // in local state, so fall back to a generic instruction. The
        // agent has the original user prompt + its previous turn in
        // `priorTurns`, which is enough context to write a useful
        // confirmation.
        const instruction = `[user decisions on proposal ${plan.planId}]\nThe ${plan.planSize} proposal(s) you previously suggested were resolved (applied or rejected) while I was offline. Please confirm the outcome based on the conversation history and let me know if any follow-up is needed.`

        await dispatch(runOntologyAgentOrchestrator({ instruction, conversationId }))
      } catch (err) {
        console.warn(
          `[agent-orchestrator] hydrate-on-load resume failed for plan ${plan.planId}`,
          err
        )
      } finally {
        inFlightResumeClaims.delete(plan.planId)
      }
    }
  }
}
